import React, { useState, useRef, useEffect } from 'react';

const BUILTIN_FONTS = [
  'ABeeZee', 'Aclonica', 'Acme', 'Actor', 'Advent Pro', 'Akronim',
  'Alegreya', 'Alegreya Sans', 'Allura', 'Amatic SC', 'Amita', 'Anton',
  'Archivo Black', 'Archivo Narrow', 'Arial', 'Bad Script', 'Bowlby One SC',
  'Bungee', 'Bungee Outline', 'Bungee Shade', 'Cinzel', 'Cinzel Decorative',
  'Comic Sans MS', 'Courier New', 'Dancing Script', 'Denk One', 'Dosis',
  'Expletus Sans', 'Fira Sans', 'Fjalla One', 'Fredoka One', 'Frijole',
  'Gentium Plus', 'Georgia', 'Germania One', 'Gruppo', 'Helvetica',
  'Homemade Apple', 'Impact', 'Imprima', 'Inter', 'Josefin Sans', 'Josefin Slab',
  'Kaushan Script', 'Khand', 'Lato', 'Libre Baskerville', 'Libre Franklin',
  'Lobster', 'Lobster Two', 'Londrina Shadow', 'Luckiest Guy', 'Merienda',
  'Old Standard TT', 'Open Sans', 'Open Sans Condensed', 'Oranienbaum',
  'Orbitron', 'Oswald', 'Pacifico', 'Palatino', 'Palanquin', 'Palanquin Dark',
  'Parisienne', 'Pattaya', 'Paytone One', 'Peddana', 'Permanent Marker',
  'Philosopher', 'Pinyon Script', 'Pirata One', 'Plaster', 'Poiret One',
  'Prompt', 'Prosto One', 'PT Sans', 'PT Sans Narrow', 'PT Serif',
  'Quicksand', 'Quintessential', 'Qwigley', 'Racing Sans One', 'Radley',
  'Raleway', 'Raleway Dots', 'Rammetto One', 'Rancho', 'Ranchers', 'Ranga',
  'Rationale', 'Ravi Prakash', 'Redressed', 'Reem Kufi', 'Roboto', 'Rochester',
  'Rock Salt', 'Rubik Mono One', 'Russo One', 'Ruthie', 'Sacramento', 'Sail',
  'Sarina', 'Sarpanch', 'Satisfy', 'Sedgwick Ave', 'Seymour One', 'Shadows Into Light',
  'Shadows Into Light Two', 'Shanti', 'Shojumaru', 'Simonetta', 'Sirin Stencil',
  'Sonsie One', 'Spicy Rice', 'StjediseSans', 'Sunshiney', 'Suranna',
  'Syncopate', 'Times New Roman', 'Tinos', 'Titan One', 'Trade Winds',
  'Trebuchet MS', 'Ubuntu Condensed', 'UnifrakturMaguntia', 'Varela Round',
  'Vast Shadow', 'Verdana', 'Waiting for the Sunrise', 'Wendy One',
  'Wire One', 'Work Sans', 'Yanone Kaffeesatz', 'Yantramanav', 'Yeseva One',
  'Yesteryear', 'Zeyada',
];

// Will be populated with custom fonts from API
let _customFontNames = [];
let _customFontsLoaded = false;

async function loadCustomFonts() {
  if (_customFontsLoaded) return _customFontNames;
  try {
    // Determine API base URL — match both /designer/ and /selenny-designer/
    const base = process.env.REACT_APP_API_URL || (window.location.pathname.includes('designer')
      ? window.location.origin + '/designer/api'
      : window.location.origin + '/api');
    const res = await fetch(base + '/fonts');
    const data = await res.json();
    if (data.fonts && data.fonts.length > 0) {
      _customFontNames = data.fonts.map(f => f.name);
      // Load CSS for custom fonts
      if (!document.getElementById('custom-fonts-css')) {
        const link = document.createElement('link');
        link.id = 'custom-fonts-css';
        link.rel = 'stylesheet';
        link.href = base + '/fonts/css';
        document.head.appendChild(link);
      }
    }
  } catch (e) {
    console.warn('Custom fonts could not be loaded:', e);
  }
  _customFontsLoaded = true;
  return _customFontNames;
}

const FONTS = BUILTIN_FONTS;

function FontPicker({ value, onChange, className }) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [allFonts, setAllFonts] = useState(BUILTIN_FONTS);
  const containerRef = useRef(null);
  const listRef = useRef(null);
  const searchRef = useRef(null);

  // Load custom fonts on mount
  useEffect(() => {
    loadCustomFonts().then(customNames => {
      if (customNames.length > 0) {
        const merged = [...new Set([...customNames, ...BUILTIN_FONTS])].sort((a, b) => a.localeCompare(b));
        setAllFonts(merged);
      }
    });
  }, []);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  // Focus search & scroll to selected when opening
  useEffect(() => {
    if (open) {
      setTimeout(() => {
        searchRef.current?.focus();
        // Scroll to selected font
        if (listRef.current) {
          const selected = listRef.current.querySelector('[data-selected="true"]');
          if (selected) selected.scrollIntoView({ block: 'center' });
        }
      }, 50);
    } else {
      setSearch('');
    }
  }, [open]);

  const filtered = search
    ? allFonts.filter(f => f.toLowerCase().includes(search.toLowerCase()))
    : allFonts;

  const handleSelect = (font) => {
    onChange(font);
    setOpen(false);
  };

  return (
    <div ref={containerRef} className={`relative ${className || ''}`}>
      {/* Trigger button */}
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="selenny-input text-xs py-2 w-full flex items-center justify-between gap-1 cursor-pointer hover:border-blue-400 transition-colors"
      >
        <span className="truncate" style={{ fontFamily: value }}>{value}</span>
        <svg className={`w-3 h-3 shrink-0 text-gray-400 transition-transform ${open ? 'rotate-180' : ''}`} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M19 9l-7 7-7-7"/></svg>
      </button>

      {/* Dropdown */}
      {open && (
        <div className="absolute z-[200] left-0 right-0 mt-1 bg-white rounded-lg shadow-2xl border border-gray-200 overflow-hidden" style={{ width: 260 }}>
          {/* Search */}
          <div className="p-2 border-b border-gray-100">
            <input
              ref={searchRef}
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Schrift suchen..."
              className="w-full text-xs px-2.5 py-1.5 rounded-md border border-gray-200 focus:outline-none focus:ring-1 focus:ring-blue-400 focus:border-blue-400"
            />
          </div>
          {/* Font list */}
          <div ref={listRef} className="overflow-y-auto" style={{ maxHeight: 280 }}>
            {filtered.length === 0 && (
              <p className="text-xs text-gray-400 text-center py-4">Keine Schrift gefunden</p>
            )}
            {filtered.map((font) => (
              <button
                key={font}
                data-selected={font === value ? 'true' : 'false'}
                onClick={() => handleSelect(font)}
                className={`w-full text-left px-3 py-2 flex items-center justify-between transition-colors ${
                  font === value
                    ? 'bg-blue-50 text-blue-700'
                    : 'text-gray-700 hover:bg-gray-50'
                }`}
              >
                <span className="text-sm truncate" style={{ fontFamily: font }}>{font}</span>
                {font === value && (
                  <svg className="w-3.5 h-3.5 text-blue-500 shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3"><path d="M20 6L9 17l-5-5"/></svg>
                )}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export { FONTS, BUILTIN_FONTS, loadCustomFonts };
export default FontPicker;
